import { numericFactCheckNotApplicable } from '../../services/reportFactCheck';
/**
 * DiffReviewDialog — Reader Polish Prompt 8 follow-up.
 *
 * Compares two report versions and lets the user pick Keep v1 or
 * Apply v2 per change. The previous dialog auto-accepted everything
 * and truncated both columns of every diff; this rewrite ships:
 *
 *   - Per-card Keep / Apply radios with severity-driven defaults.
 *   - A "Needs attention" section for regressions ≥ 20pt and verdict
 *     flips, sorted to the top.
 *   - Inline word-level diff (no side-by-side truncation).
 *   - Identical-content dedup with "(applies to: A, B)" footnote.
 *   - "Why" line per card from the rerun trace where available.
 *   - Apply blocked until every flagged card has a decision.
 *   - Quality-regression confirm above Apply when the user is about
 *     to drop a quality score by > 20pt.
 *   - No "Accept All" button — the per-card radios + counter make it
 *     redundant.
 */
import React, { useMemo, useState } from 'react';
import { useStore } from '../../store';
import type { SiteRagReport } from '../../types/siteRag';
import { Button } from '../ui/button';
import { X, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { classifyDiff, type DiffSeverity, type SeverityDecision } from '../../lib/diffSeverity';
import { dedupIdenticalDiffs, type DedupItem } from '../../lib/diffDedup';
import { ReviewChangeCard, type ReviewDecision } from './diff/ReviewChangeCard';
import { emitAuditEvent } from '../../lib/auditLogClient';

interface Props {
  oldReport: SiteRagReport;
  newReport: SiteRagReport;
  open: boolean;
  onClose: () => void;
}

interface RawDiff extends DedupItem {
  section: string;
  label: string;
  oldValue: string;
  newValue: string;
  type: 'score' | 'text' | 'status';
}

/**
 * Build the raw diff list — same field-by-field comparison the
 * previous dialog used, but without the per-cell truncation. The
 * full string is preserved; the rendering layer caps long bodies.
 */
function buildRawDiffs(oldReport: SiteRagReport, newReport: SiteRagReport): RawDiff[] {
  const items: RawDiff[] = [];

  if ((oldReport.overallScore ?? 0) !== (newReport.overallScore ?? 0)) {
    items.push({ section: 'overallScore', label: 'Overall Score', oldValue: `${oldReport.overallScore ?? 0}/100`, newValue: `${newReport.overallScore ?? 0}/100`, type: 'score' });
  }
  if ((oldReport.overallStatus || '') !== (newReport.overallStatus || '')) {
    items.push({ section: 'overallStatus', label: 'Status', oldValue: (oldReport.overallStatus || 'N/A').toUpperCase(), newValue: (newReport.overallStatus || 'N/A').toUpperCase(), type: 'status' });
  }
  if (oldReport.conclusion?.recommendation !== newReport.conclusion?.recommendation) {
    items.push({ section: 'conclusion', label: 'Verdict', oldValue: oldReport.conclusion?.recommendation || 'N/A', newValue: newReport.conclusion?.recommendation || 'N/A', type: 'status' });
  }
  if (oldReport.executiveSummary !== newReport.executiveSummary && newReport.executiveSummary) {
    items.push({ section: 'executiveSummary', label: 'Executive Summary', oldValue: oldReport.executiveSummary || '', newValue: newReport.executiveSummary, type: 'text' });
  }
  if (oldReport.sitingNotes !== newReport.sitingNotes && newReport.sitingNotes) {
    items.push({ section: 'sitingNotes', label: 'Siting Notes', oldValue: oldReport.sitingNotes || '', newValue: newReport.sitingNotes, type: 'text' });
  }
  for (const newCat of newReport.categories || []) {
    const oldCat = oldReport.categories?.find(c => c.id === newCat.id);
    if (oldCat && oldCat.score !== newCat.score) {
      items.push({ section: `cat_${newCat.id}`, label: newCat.label, oldValue: `${oldCat.score}/100 (${oldCat.status})`, newValue: `${newCat.score}/100 (${newCat.status})`, type: 'score' });
    }
  }
  if ((oldReport.risks?.length || 0) !== (newReport.risks?.length || 0)) {
    items.push({ section: 'risks', label: 'Risk Count', oldValue: `${oldReport.risks?.length || 0} risks`, newValue: `${newReport.risks?.length || 0} risks`, type: 'score' });
  }
  if (oldReport.dataQuality !== newReport.dataQuality) {
    items.push({ section: 'dataQuality', label: 'Data Quality', oldValue: oldReport.dataQuality || 'N/A', newValue: newReport.dataQuality || 'N/A', type: 'status' });
  }
  if (oldReport.sedCompleteness !== newReport.sedCompleteness) {
    items.push({ section: 'sedCompleteness', label: 'Evidence', oldValue: `${Math.round((oldReport.sedCompleteness || 0) * 100)}%`, newValue: `${Math.round((newReport.sedCompleteness || 0) * 100)}%`, type: 'score' });
  }
  if (oldReport.summary !== newReport.summary && newReport.summary) {
    items.push({ section: 'summary', label: 'Summary', oldValue: oldReport.summary || '', newValue: newReport.summary, type: 'text' });
  }
  const oldFc = oldReport.factCheck?.score ?? 0;
  const newFc = newReport.factCheck?.score ?? 0;
  if (oldFc !== newFc && !numericFactCheckNotApplicable(oldReport.factCheck) && !numericFactCheckNotApplicable(newReport.factCheck)) {
    items.push({ section: 'factCheck', label: 'Fact-Check Score', oldValue: `${oldFc}%`, newValue: `${newFc}%`, type: 'score' });
  }
  const oldQr = oldReport.qualityReview?.score ?? 0;
  const newQr = newReport.qualityReview?.score ?? 0;
  if (oldQr !== newQr) {
    items.push({ section: 'qualityReview', label: 'AI Quality Score', oldValue: `${oldQr}/100`, newValue: `${newQr}/100`, type: 'score' });
  }
  if ((oldReport.documents?.length ?? 0) !== (newReport.documents?.length ?? 0)) {
    items.push({ section: 'documents', label: 'Documents Used', oldValue: `${oldReport.documents?.length ?? 0}`, newValue: `${newReport.documents?.length ?? 0}`, type: 'score' });
  }
  if (oldReport.insights?.powerStrategy !== newReport.insights?.powerStrategy && newReport.insights?.powerStrategy) {
    items.push({ section: 'powerStrategy', label: 'Power Strategy', oldValue: oldReport.insights?.powerStrategy || '', newValue: newReport.insights?.powerStrategy, type: 'text' });
  }
  if (oldReport.insights?.coolingStrategy !== newReport.insights?.coolingStrategy && newReport.insights?.coolingStrategy) {
    items.push({ section: 'coolingStrategy', label: 'Cooling Strategy', oldValue: oldReport.insights?.coolingStrategy || '', newValue: newReport.insights?.coolingStrategy, type: 'text' });
  }
  const oldGrid = oldReport.gridStudy?.interconnectionAssessment || '';
  const newGrid = newReport.gridStudy?.interconnectionAssessment || '';
  if (oldGrid !== newGrid && newGrid) {
    items.push({ section: 'gridStudy', label: 'Grid Power Study', oldValue: oldGrid || 'Not generated', newValue: newGrid, type: 'text' });
  }
  const oldTrans = oldReport.transportStudy?.overview || '';
  const newTrans = newReport.transportStudy?.overview || '';
  if (oldTrans !== newTrans && newTrans) {
    items.push({ section: 'transportStudy', label: 'Transport & Logistics', oldValue: oldTrans || 'Not generated', newValue: newTrans, type: 'text' });
  }
  const oldConn = oldReport.connectivityStudy?.overview || '';
  const newConn = newReport.connectivityStudy?.overview || '';
  if (oldConn !== newConn && newConn) {
    items.push({ section: 'connectivityStudy', label: 'Connectivity & Fiber', oldValue: oldConn || 'Not generated', newValue: newConn, type: 'text' });
  }
  if ((oldReport.developmentRisks?.length || 0) !== (newReport.developmentRisks?.length || 0)) {
    items.push({ section: 'riskAssessment', label: 'Risk Assessment', oldValue: `${oldReport.developmentRisks?.length || 0} development risks`, newValue: `${newReport.developmentRisks?.length || 0} development risks`, type: 'score' });
  }
  const oldPathwayCount = oldReport.developmentPathway?.length || 0;
  const newPathwayCount = newReport.developmentPathway?.length || 0;
  const oldRecCount = oldReport.recommendations?.length || 0;
  const newRecCount = newReport.recommendations?.length || 0;
  if (oldPathwayCount !== newPathwayCount || oldRecCount !== newRecCount) {
    items.push({ section: 'actionPlan', label: 'Action Plan', oldValue: `${oldPathwayCount} phases, ${oldRecCount} recommendations`, newValue: `${newPathwayCount} phases, ${newRecCount} recommendations`, type: 'score' });
  }

  return items;
}

/**
 * Pull a "Why" string from rerun trace metadata. Today this inspects
 * `versionNote` on the new report and — Cost P5 — the incremental
 * carry-forward telemetry: when this report was generated incrementally
 * (`regeneratedSections` present), only the regenerated sections changed;
 * the rest were carried forward verbatim. So the diff list NATURALLY shows
 * only the regenerated sections (carried-forward sections are byte-identical
 * → no diff). This surfaces that provenance on the card.
 */
function whyForChange(newReport: SiteRagReport, section: string): string | undefined {
  const regenerated = newReport.regeneratedSections;
  if (regenerated && regenerated.length > 0 && regenerated.includes(section)) {
    const note = newReport.versionNote?.trim();
    return note
      ? `Regenerated section (${regenerated.length} regenerated, rest carried forward). ${note}`
      : `Regenerated section — the rest of the report was carried forward unchanged.`;
  }
  return newReport.versionNote && newReport.versionNote.trim().length > 0
    ? newReport.versionNote
    : undefined;
}

interface ResolvedDiff {
  section: string;
  label: string;
  oldValue: string;
  newValue: string;
  type: 'score' | 'text' | 'status';
  severity: DiffSeverity;
  badge?: string;
  delta?: number;
  appliesTo: string[];
  why?: string;
}

export function DiffReviewDialog({ oldReport, newReport, open, onClose }: Props) {
  const { updateRagReport } = useStore();
  const [isApplying, setIsApplying] = useState(false);
  const [confirmedRegression, setConfirmedRegression] = useState(false);

  const diffs = useMemo<ResolvedDiff[]>(() => {
    const raw = buildRawDiffs(oldReport, newReport);
    const deduped = dedupIdenticalDiffs(raw);
    const resolved: ResolvedDiff[] = deduped.map(({ primary, appliesTo }) => {
      const sev: SeverityDecision = classifyDiff(primary.section, primary.oldValue, primary.newValue);
      return {
        section: primary.section,
        label: primary.label,
        oldValue: primary.oldValue,
        newValue: primary.newValue,
        type: primary.type,
        severity: sev.severity,
        badge: sev.badge,
        delta: sev.delta,
        appliesTo,
        why: whyForChange(newReport, primary.section),
      };
    });
    // Sort: 'attention' first, preserving original order within groups.
    resolved.sort((a, b) => {
      if (a.severity === b.severity) return 0;
      return a.severity === 'attention' ? -1 : 1;
    });
    return resolved;
  }, [oldReport, newReport]);

  // Default decision per card. Captured separately from the live
  // state so the telemetry layer can detect "user toggled at least
  // one card away from default" vs "untouched defaults" when the
  // dialog closes.
  const defaultDecisions = useMemo<Record<string, ReviewDecision>>(() => {
    const init: Record<string, ReviewDecision> = {};
    for (const d of diffs) init[d.section] = d.severity === 'attention' ? 'keep' : 'apply';
    return init;
  }, [diffs]);

  // Decision state — keyed by section. Defaults: attention → keep,
  // normal → apply.
  const [decisions, setDecisions] = useState<Record<string, ReviewDecision>>(() => ({ ...defaultDecisions }));

  const setDecision = (section: string, next: ReviewDecision) => {
    setDecisions(prev => ({ ...prev, [section]: next }));
    setConfirmedRegression(false);
  };

  const attentionItems = diffs.filter(d => d.severity === 'attention');
  const normalItems = diffs.filter(d => d.severity === 'normal');

  // Apply gating: the user must have toggled (or kept the default
  // for) every attention card. The default IS a decision, so this
  // gate currently passes immediately; the user can still choose to
  // change the default. The Apply counter shows how many of the
  // attention cards would actually flip from Keep → Apply.
  const attentionUnresolved = attentionItems.filter(d => decisions[d.section] === undefined).length;

  // Quality regression confirm: surfaces when the user has chosen
  // 'apply' on a quality / fact-check / overall-score regression of
  // > 20pt. Until they tap Confirm regression, Apply is disabled.
  const pendingRegression = attentionItems.find(d =>
    decisions[d.section] === 'apply'
    && typeof d.delta === 'number'
    && d.delta <= -20,
  );

  const applyChangeCount = diffs.filter(d => decisions[d.section] === 'apply').length;
  const keepChangeCount = diffs.filter(d => decisions[d.section] === 'keep').length;
  const applyDisabled = isApplying
    || attentionUnresolved > 0
    || (pendingRegression !== undefined && !confirmedRegression);

  // Build a compact decisions map for telemetry — one entry per
  // card with the user's final decision and whether it deviates
  // from the default. Drives both apply + cancel audit emissions.
  const buildDecisionsLog = () => {
    return diffs.map(d => ({
      section: d.section,
      severity: d.severity,
      decision: decisions[d.section] ?? defaultDecisions[d.section] ?? 'apply',
      defaultDecision: defaultDecisions[d.section] ?? 'apply',
      deviated: (decisions[d.section] ?? defaultDecisions[d.section]) !== defaultDecisions[d.section],
      ...(typeof d.delta === 'number' ? { delta: d.delta } : {}),
    }));
  };

  // Wrap the parent's onClose so dismissal goes through telemetry.
  // We distinguish "decided" (toggled at least one card away from
  // default) from "dismissed" (untouched defaults) — both useful
  // for product instrumentation.
  const handleDialogClose = () => {
    if (diffs.length > 0) {
      const decisionsLog = buildDecisionsLog();
      const deviatedCount = decisionsLog.filter(d => d.deviated).length;
      void emitAuditEvent({
        action: 'review-changes-decision',
        target: { type: 'report', id: newReport.id, label: newReport.siteName },
        metadata: {
          surface: deviatedCount > 0 ? 'cancel-after-decisions' : 'cancel-dismissed',
          oldVersion: oldReport.version ?? 1,
          newVersion: newReport.version ?? 2,
          totalCards: decisionsLog.length,
          attentionCards: decisionsLog.filter(d => d.severity === 'attention').length,
          deviatedCount,
          decisions: decisionsLog,
        },
      });
    }
    onClose();
  };

  const handleApply = async () => {
    setIsApplying(true);
    try {
      // For 'keep' decisions on text fields, revert the new report
      // to the old value for that field. Score / status / count
      // diffs aren't reverted — they're computed downstream and
      // would re-derive on next render.
      const reverts: Partial<SiteRagReport> = {};
      for (const d of diffs) {
        if (decisions[d.section] !== 'keep') continue;
        if (d.section === 'executiveSummary') reverts.executiveSummary = oldReport.executiveSummary;
        else if (d.section === 'sitingNotes') reverts.sitingNotes = oldReport.sitingNotes;
        else if (d.section === 'summary')      reverts.summary = oldReport.summary;
        else if (d.section === 'powerStrategy' || d.section === 'coolingStrategy') {
          reverts.insights = {
            ...(reverts.insights ?? newReport.insights ?? {} as never),
            ...(d.section === 'powerStrategy'
              ? { powerStrategy: oldReport.insights?.powerStrategy ?? '' }
              : { coolingStrategy: oldReport.insights?.coolingStrategy ?? '' }),
          };
        }
      }
      if (Object.keys(reverts).length > 0) {
        await updateRagReport(newReport.id, reverts);
      }
      // Apply audit — captures per-card decisions plus the
      // regression-confirmed flag so we can measure how often users
      // actually scrutinise these changes vs rubber-stamp them.
      const decisionsLog = buildDecisionsLog();
      void emitAuditEvent({
        action: 'review-changes-decision',
        target: { type: 'report', id: newReport.id, label: newReport.siteName },
        metadata: {
          surface: 'apply',
          oldVersion: oldReport.version ?? 1,
          newVersion: newReport.version ?? 2,
          totalCards: decisionsLog.length,
          applyCount: applyChangeCount,
          keepCount: keepChangeCount,
          attentionCards: decisionsLog.filter(d => d.severity === 'attention').length,
          regressionConfirmed: confirmedRegression,
          deviatedCount: decisionsLog.filter(d => d.deviated).length,
          decisions: decisionsLog,
        },
      });
      toast.success(`Applied ${applyChangeCount} change${applyChangeCount === 1 ? '' : 's'}, kept ${keepChangeCount}`);
      useStore.getState().setActiveRagReport(newReport.id);
      onClose();
    } catch (e) {
      toast.error(`Failed to apply: ${(e as Error).message}`);
    } finally {
      setIsApplying(false);
    }
  };

  if (!open) return null;

  // No differences detected — show summary and auto-switch.
  if (diffs.length === 0) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={handleDialogClose}>
        <div className="bg-background w-[calc(100vw-2rem)] max-w-[480px] shadow-2xl border border-border p-6 space-y-4" onClick={e => e.stopPropagation()}>
          <h2 className="text-sm font-bold uppercase tracking-widest text-foreground">Report Updated</h2>
          <p className="text-[10px] text-text-muted">
            v{oldReport.version || 1} → v{newReport.version || 2} — no significant changes detected.
          </p>
          <Button size="sm" className="w-full h-9 type-meta font-bold" onClick={() => {
            useStore.getState().setActiveRagReport(newReport.id);
            onClose();
          }}>
            View Updated Report
          </Button>
        </div>
      </div>
    );
  }

  const applyLabel = pendingRegression && !confirmedRegression
    ? `Confirm regression to apply (${applyChangeCount})`
    : applyChangeCount === 0
      ? 'Apply (nothing to apply)'
      : `Apply ${applyChangeCount} change${applyChangeCount === 1 ? '' : 's'}`;

  const ctaCounterMsg = attentionUnresolved > 0
    ? `${attentionUnresolved} need${attentionUnresolved === 1 ? 's' : ''} your decision`
    : `${applyChangeCount} apply · ${keepChangeCount} keep`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={handleDialogClose}>
      <div className="bg-background w-[calc(100vw-2rem)] max-w-[720px] min-w-0 max-h-[85vh] overflow-x-auto overflow-y-auto shadow-2xl border border-border" onClick={e => e.stopPropagation()} data-testid="review-changes-dialog">
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div>
            <h2 className="text-sm font-bold uppercase tracking-widest text-foreground">Review Changes</h2>
            <p className="type-meta text-text-secondary uppercase tracking-widest mt-0.5">
              v{oldReport.version || 1} → v{newReport.version || 2} — {diffs.length} change{diffs.length === 1 ? '' : 's'} detected
            </p>
          </div>
          <button onClick={handleDialogClose} className="p-1 text-text-secondary hover:text-text-primary" data-testid="review-changes-close"><X className="w-4 h-4" /></button>
        </div>

        <div className="p-4 space-y-4">
          {attentionItems.length > 0 && (
            <section className="space-y-2" data-testid="review-attention-section">
              <h3 className="flex items-center gap-2 type-section text-warning">
                <AlertTriangle className="w-4 h-4" aria-hidden="true" />
                Needs attention ({attentionItems.length})
              </h3>
              {attentionItems.map(d => (
                <ReviewChangeCard
                  key={d.section}
                  section={d.section}
                  label={d.label}
                  oldValue={d.oldValue}
                  newValue={d.newValue}
                  severity={d.severity}
                  badge={d.badge}
                  appliesTo={d.appliesTo}
                  why={d.why}
                  isScore={d.type === 'score' || d.type === 'status'}
                  decision={decisions[d.section] ?? 'keep'}
                  onDecide={next => setDecision(d.section, next)}
                />
              ))}
            </section>
          )}

          {normalItems.length > 0 && (
            <section className="space-y-2" data-testid="review-normal-section">
              {attentionItems.length > 0 && (
                <h3 className="type-section text-text-secondary">
                  Other changes ({normalItems.length})
                </h3>
              )}
              {normalItems.map(d => (
                <ReviewChangeCard
                  key={d.section}
                  section={d.section}
                  label={d.label}
                  oldValue={d.oldValue}
                  newValue={d.newValue}
                  severity={d.severity}
                  appliesTo={d.appliesTo}
                  why={d.why}
                  isScore={d.type === 'score' || d.type === 'status'}
                  decision={decisions[d.section] ?? 'apply'}
                  onDecide={next => setDecision(d.section, next)}
                />
              ))}
            </section>
          )}
        </div>

        <div className="flex flex-col gap-2 p-4 border-t border-border">
          {pendingRegression && (
            <div
              className="flex items-start gap-2 px-3 py-2 rounded-lg border border-warning/40 bg-warning/10 text-[10px] text-text-primary"
              data-testid="review-regression-confirm"
            >
              <AlertTriangle className="w-4 h-4 text-warning shrink-0 mt-0.5" aria-hidden="true" />
              <div className="flex-1 min-w-0">
                Applying this will lower {pendingRegression.label} from {pendingRegression.oldValue} to {pendingRegression.newValue}.
              </div>
              <button
                type="button"
                onClick={() => setConfirmedRegression(true)}
                disabled={confirmedRegression}
                className="type-meta text-warning rounded-md border border-warning/40 px-2 py-1 hover:bg-warning/15 disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-warning focus-visible:ring-offset-1"
                data-testid="review-regression-confirm-button"
              >
                {confirmedRegression ? 'Confirmed' : 'Confirm regression'}
              </button>
            </div>
          )}
          <div className="flex items-center justify-between">
            <span className="text-[10px] text-text-muted uppercase tracking-widest" data-testid="review-counter">
              {ctaCounterMsg}
            </span>
            <Button
              size="sm"
              className="h-9 type-meta font-bold"
              onClick={handleApply}
              disabled={applyDisabled}
              data-testid="review-apply"
            >
              {isApplying ? 'Applying…' : applyLabel}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
